<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import Plane from "@/Components/Generics/Plane.vue";
import { Event } from "@/Types";
import { defineComponent, PropType } from "vue";

const WEEKDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function pad(n) {
    return String(n).padStart(2, "0");
}

export default defineComponent({
    name: "YearCalendar",
    components: {
        Plane,
        GenericButton,
    },
    props: {
        events: {
            type: Array as PropType<Event[]>,
            default: () => [],
        },
        year: {
            type: Number,
            default: () => new Date().getFullYear(),
        },
    },
    emits: ["change-year"],
    data() {
        const today = new Date();
        return {
            today,
            currentMonth: today.getFullYear() === this.year ? today.getMonth() : -1,
            selectedMonth: today.getFullYear() === this.year ? today.getMonth() : 0,
            months: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
            dayHeaders: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
        };
    },
    computed: {
        isCurrentYear(): boolean {
            return this.year === new Date().getFullYear();
        },
        selectedMonthName(): string {
            return this.months[this.selectedMonth];
        },
        eventsByDate() {
            const map = {};
            for (const ev of this.events) {
                const start = new Date(ev.starting_at);
                const end = new Date(ev.ending_at);
                const cur = new Date(start);
                while (cur <= end) {
                    const ds = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`;
                    map[ds] = map[ds] || [];
                    map[ds].push({ type: "event", event: ev });
                    cur.setDate(cur.getDate() + 1);
                }
            }
            return map;
        },

        panelEvents() {
            const m = this.selectedMonth;
            const result = [];

            for (const ev of this.events) {
                const start = new Date(ev.starting_at);
                const end = new Date(ev.ending_at);
                const monthStart = new Date(this.year, m, 1);
                const monthEnd = new Date(this.year, m + 1, 0);
                if (start <= monthEnd && end >= monthStart) {
                    const isMultiDay = ev.starting_at !== ev.ending_at;
                    result.push({
                        ...ev,
                        date: start,
                        day: pad(start.getDate()),
                        weekday: WEEKDAYS[start.getDay()],
                        name: ev.name || ev.organizer_name || "Event",
                        isMultiDay,
                        endDay: isMultiDay ? pad(end.getDate()) : null,
                        endWeekday: isMultiDay ? WEEKDAYS[end.getDay()] : null,
                        endMonth: isMultiDay ? this.months[end.getMonth()] : null,
                    });
                }
            }

            return result.sort((a, b) => a.date - b.date);
        },
    },

    methods: {
        selectMonth(m: number): void {
            this.selectedMonth = m;
        },

        getDaysInMonth(m: number): number {
            return new Date(this.year, m + 1, 0).getDate();
        },
        getStartOffset(m: number): number {
            let dow = new Date(this.year, m, 1).getDay();
            if (dow === 0) dow = 7;
            return dow - 1;
        },
        dateString(m: number, d: number): string {
            return `${this.year}-${pad(m + 1)}-${pad(d)}`;
        },

        getDayClass(m: number, d: number): string {
            const ds = this.dateString(m, d);
            const dow = new Date(this.year, m, d).getDay();
            const isToday = m === this.currentMonth && d === this.today.getDate() && this.today.getFullYear() === this.year;
            const hasEvent = !!(this.eventsByDate[ds] || []).length;
            const isWeekend = dow === 0 || dow === 6;

            if (isToday) return "dc-today rounded-circle";
            if (hasEvent) return "dc-event rounded-1";
            if (isWeekend) return "text-weekend";
            return "text-day";
        },

        hasEventInMonth(m: number): boolean {
            return this.countEventsInMonth(m) > 0;
        },

        countEventsInMonth(m: number): number {
            const monthStart = new Date(this.year, m, 1);
            const monthEnd = new Date(this.year, m + 1, 0);
            return this.events.filter((ev) => {
                const start = new Date(ev.starting_at);
                const end = new Date(ev.ending_at);
                return start <= monthEnd && end >= monthStart;
            }).length;
        },

        clickLoginSchool(): void {
            this.$inertia.visit(route("login"));
        },
    },
});
</script>

<template>
    <div class="flex flex--width flex--v flex--valign-c flex--gap-50">
        <div class="flex flex--width flex--v flex--valign-c flex--gap-30">
            <!-- <Plane :darker="true"> -->
            <div class="pagination-new flex--width flex flex--halign-c" aria-label="Jahresnavigation">
                <nav class="pagination-new-nav flex flex--valign-c flex--gap-30">
                    <button class="pagination-button" aria-label="Vorheriges Jahr" @click="$emit('change-year', year - 1)">
                        <span class="pagination-button-text pagination-button-text--arrow text text--accent-gray-a text--size-40 text--bold">
                            {{ "<" }}
                        </span>
                    </button>

                    <div class="pagination-buttons-inner flex flex--valign-c flex--gap-0">
                        <!-- <button v-for="page in visiblePages" :key="page.key" class="pagination-button" :class="{ active: page.page === currentPage, dots: page.isDots }" :disabled="page.isDots" @click="goToPage(page.page)"> -->
                        <span class="pagination-button-text text text--primary-b text--size-22 text--medium">
                            {{ year }}
                        </span>
                        <!-- </button> -->
                    </div>

                    <button class="pagination-button" aria-label="Nächstes Jahr" @click="$emit('change-year', year + 1)">
                        <span class="pagination-button-text pagination-button-text--arrow text text--accent-gray-a text--size-40 text--bold">
                            {{ ">" }}
                        </span>
                    </button>
                </nav>

                <div class="pagination-header-buttons flex flex--halign-sb pe-none">
                    <GenericButton class="pagination-header-button position-absolute start-0" :title="$trans('auth.login_school')" :secondary="true" :circular="true" @click="clickLoginSchool()" />
                    <GenericButton
                        class="pagination-header-button position-absolute end-0"
                        :title="$trans('event.jump_to_today')"
                        :circular="true"
                        :style="{ visibility: isCurrentYear ? 'hidden' : 'visible' }"
                        @click="$emit('change-year', new Date().getFullYear())" />
                </div>
            </div>
            <!-- </Plane> -->

            <div class="flex--width d-grid cal-layout gap-3 align-items-start">
                <!-- Event-Panel -->
                <Plane class="panel-bg" :darker="true">
                    <div class="flex flex--width flex--v flex--valign-c flex--gap-30">
                        <div class="flex--width text text-uppercase text--primary-b text--size-20 text--bold">
                            <i class="bi bi-calendar-event"></i>
                            {{ selectedMonthName }}
                        </div>

                        <span v-if="panelEvents.length === 0" class="panel-muted panel-muted--empty text text--accent-gray-b text--size-16 text--medium text-center">
                            {{ $trans("event.no_events") }}
                        </span>

                        <div v-else class="pb-3 flex flex--width flex--v flex--valign-c flex--gap-10">
                            <div v-for="(ev, i) in panelEvents" :key="i" class="flex flex--gap-10" :class="{ 'border-top panel-divider': i > 0 }">
                                <span class="event-dot mt-1 flex-shrink-0 rounded-circle dot-event"></span>
                                <div class="flex flex--v flex--gap-8 flex--valign-s">
                                    <span class="panel-muted text text--accent-gray-b text--size-16 text--medium">
                                        {{ ev.weekday }}, {{ ev.day }}. {{ selectedMonthName }}
                                        <template v-if="ev.isMultiDay">&ndash; {{ ev.endWeekday }}, {{ ev.endDay }}. {{ ev.endMonth }}</template>
                                    </span>
                                    <span class="panel-name-text text text--primary-a text--size-18 text--semi-bold">
                                        {{ ev.name }}
                                        <br />
                                        {{ ev.organizer_name }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Plane>

                <!-- Monatskalender -->
                <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                    <div v-for="(month, m) in months" :key="m" class="col">
                        <Plane
                            class="h-100 month-card"
                            :class="{
                                'month-selected': selectedMonth === m,
                                'month-current': m === currentMonth && selectedMonth !== m,
                            }"
                            role="button"
                            tabindex="0"
                            @click="selectMonth(m)"
                            @keydown.enter="selectMonth(m)"
                            @keydown.space.prevent="selectMonth(m)">
                            <div class="p-2">
                                <div class="mb-1 month-name-text text text--size-18 text--medium flex flex--valign-c flex--gap-5">
                                    {{ month }}
                                    <span v-if="hasEventInMonth(m)" class="month-event-badge fs-6 ms-2 flex-shrink-0 text text--bold text--size-24">{{ countEventsInMonth(m) }}</span>
                                </div>
                                <div class="day-grid">
                                    <div v-for="(day, i) in dayHeaders" :key="'h' + i" class="dc text text--size-18 flex flex--valign-c flex--gap-10 justify-content-center text-day-header dc-header" :class="i >= 5 ? 'text-weekend' : ''">
                                        {{ day }}
                                    </div>
                                    <div v-for="n in getStartOffset(m)" :key="'e' + n" class="dc" />
                                    <div v-for="d in getDaysInMonth(m)" :key="d" class="dc dc-day text text--size-16 flex flex--valign-c flex--gap-10 justify-content-center" :class="getDayClass(m, d)">
                                        {{ d }}
                                    </div>
                                </div>
                            </div>
                        </Plane>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.pagination-header-buttons {
    position: absolute;
    inset: 0;
}
@include media-breakpoint-down(md) {
    .pagination-new {
        flex-direction: column;
        align-items: center;
        gap: rem(5);
        margin-bottom: rem(-15);

        .pagination-new-nav {
            width: fit-content;
        }

        .pagination-header-buttons {
            position: relative;
            width: 100%;

            .pagination-header-button {
                position: relative !important;
                max-width: 100%;
            }
        }
    }
}
@include media-breakpoint-down(sm) {
    .pagination-new {
        margin-bottom: rem(-25);

        .pagination-header-buttons {
            flex-direction: column;
            width: fit-content;
            gap: rem(5);

            .pagination-header-button {
                position: relative !important;
                max-width: 100%;
            }
        }
    }
}

.cal-layout {
    grid-template-columns: minmax(#{rem(200)}, #{rem(280)}) minmax(0, 1fr);
    align-items: start;

    @include media-breakpoint-down(xl) {
        // grid-template-columns: 2fr;
        grid-template-columns: minmax(#{rem(100)}, #{rem(200)}) minmax(0, 1fr);
    }

    @include media-breakpoint-down(md) {
        grid-template-columns: 1fr;
    }
}

// Panel
.panel-bg {
    padding: rem(25);
    border: rem(1) solid #ffffff12;
    position: sticky;
    top: rem(70);
    max-height: calc(100vh - 4rem);
    overflow-y: auto;

    @include media-breakpoint-down(md) {
        position: static;
        max-height: none;
        overflow-y: visible;
    }
}
.panel-title-text {
    color: $font-turquoise;
}
.panel-muted {
    // color: rgba(255, 255, 255, 0.35);
    max-width: 67%;
    &.panel-muted--empty {
        padding-bottom: rem(30);
    }
}
.panel-name-text {
    // color: $font-creamy;
    // line-height: 1.4;
}
.panel-divider {
    border-top: rem(2) solid $color-accent-gray-a !important;
    padding-top: rem(10);
}
.event-dot {
    width: rem(7);
    height: rem(7);
    display: inline-block;
    // background: $font-creamy;
    background-color: $color-primary-b;
}

// Month cards
.month-card {
    container-type: inline-size;
    cursor: pointer;
    background-color: $color-primary-b-darker;
    border-radius: 0.375rem;
    outline: rem(2) solid transparent;
    transition:
        background-color 0.15s,
        outline-color 0.15s;
    padding: rem(20);

    &:hover {
        background-color: $color-primary-b;
        border-color: #ffffff26;
    }

    &.month-selected {
        outline-color: $color-primary-a;
        background-color: $color-primary-b;
    }

    &.month-current {
        border-color: rgba($font-turquoise, 0.4);
    }

    &:focus-visible {
        outline: rem(2) solid $font-turquoise;
        outline-offset: rem(2);
    }
}

.month-name-text {
    color: rgba(255, 255, 255, 0.85);
    width: fit-content;
    position: relative;
}

.month-event-badge {
    position: absolute;
    left: 100%;

    color: $color-primary-b;
    background: $color-secondary-a;
    border-radius: rem(999);
    padding: rem(1) rem(5);
    line-height: 1.5;
}

// Day grid
.day-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: rem(1);
}

.dc {
    aspect-ratio: 1;
}

.dc-header {
    font-size: clamp(1rem, 3cqi, 1.5rem);
    font-weight: 500;
}

.dc-day {
    font-size: clamp(1rem, 4cqi, 1.5rem);
}

.text-day {
    color: $color-accent-light-a;
}

.text-day-header {
    color: $color-accent-light-c;
}

.text-weekend {
    color: $color-secondary-b;
}

.dc-today {
    background: $font-turquoise;
    color: $background-blue;
    font-weight: 600;
}
.dc-event {
    background: rgba($font-creamy, 0.15);
    color: $font-creamy;
    font-weight: 500;
}
</style>
